import { useNavigate, useParams } from "react-router-dom";
import useDocumentTitle from "../../util/useDocumentTitle";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import { UserGroup } from "../../common/User";
import { useGetVesselQuery, useGetVesselsQuery } from "../../redux/api/vessel";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  TextField,
} from "@mui/material";
import { colorFromName } from "../../common/ColorFromName";
import { Vessel } from "../../common/Vessel";
import ComplianceTrainingRosterForm from "./ComplianceTrainingRosterForm";
import ComplianceTrainingSettingsModal from "./ComplianceTrainingSettingsModal";
import ComplianceTrainingUploadModal from "./ComplianceTrainingUploadModal";
import { useGetComplianceRosterQuery } from "../../redux/api/complianceTraining";
import { JobRating } from "../../common/JobOrder";
import { ComplianceRosterRow } from "../../common/ComplianceTraining";

const ComplianceTrainingRosterComponent = () => {
  useDocumentTitle("Compliance Training Roster");
  const { id } = useParams();
  const navigate = useNavigate();

  const user = useSelector(selectCurrentUser);

  const { data: vesselOptions, isLoading, isFetching } = useGetVesselsQuery({});
  const { data: vessel } = useGetVesselQuery(
    id !== undefined && id !== "" && id !== "new" ? id : skipToken
  );
  const { data: rawRosterResponse } = useGetComplianceRosterQuery(
    id !== undefined && id !== "" ? id : skipToken
  );

  const galleyRatings = [
    JobRating._CHC,
    JobRating._2CB,
    JobRating._AC,
    JobRating._SA,
    JobRating._STB,
    JobRating._STW,
  ];

  const rosterResponse = rawRosterResponse
    ? {
        ...rawRosterResponse,
        rows: [...rawRosterResponse.rows].sort((a: ComplianceRosterRow, b: ComplianceRosterRow) => {
          if (a.rating === b.rating) return 0;
          const aGalley = galleyRatings.includes(a.rating as JobRating);
          const bGalley = galleyRatings.includes(b.rating as JobRating);
          if (aGalley !== bGalley) return aGalley ? 1 : -1;
          return (
            Object.keys(JobRating).indexOf(a.rating) -
            Object.keys(JobRating).indexOf(b.rating)
          );
        }),
      }
    : undefined;

  const changeVessel = (vesselId: number | undefined) => {
    if (vesselId != null) {
      navigate(`/complianceRoster/${vesselId}`);
    }
  };

  useEffect(() => {
    if (vesselOptions?.length === 1) {
      changeVessel(vesselOptions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselOptions]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {!isLoading && !isFetching && (
        <>
          <div className="roster-page-header mb-0">
            <div className="header-group">
              <h1>Compliance Training Roster</h1>
              <Avatar
                sx={{
                  bgcolor: user ? colorFromName(user.name) : colorFromName(""),
                  height: 24,
                  width: 24,
                  fontSize: 16,
                }}
                alt={user ? user.name : ""}
                src="/"
              />
              {!!vesselOptions?.length && vesselOptions?.length === 1 && (
                <h3>{vessel?.name}</h3>
              )}
              {!!vesselOptions?.length && vesselOptions?.length > 1 && (
                <Autocomplete
                  className="vessel-dropdown"
                  id="vesselOptions"
                  options={
                    vesselOptions
                      ? (vesselOptions as Vessel[]).filter((v) => !v.disabled)
                      : []
                  }
                  value={vessel ? vessel : null}
                  onChange={(event, value) => changeVessel(value?.id)}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Pick a Vessel" />
                  )}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  getOptionLabel={(opt) => (opt.name ? opt.name : "")}
                />
              )}
              <div>
                {!!id && (user?.userGroup as UserGroup) !== UserGroup.FLEET_EXECS && (user?.userGroup as UserGroup) !== UserGroup.VESSEL_EXECS && (
                  <ComplianceTrainingUploadModal rosterResponse={rosterResponse} />
                )}
                {((user?.userGroup as UserGroup) === UserGroup.CREWING_MANAGERS ||
                  (user?.userGroup as UserGroup) === UserGroup.HUMAN_RESOURCES_ADMINS) && (
                  <ComplianceTrainingSettingsModal />
                )}
              </div>
            </div>
          </div>
          {!!id && <ComplianceTrainingRosterForm rosterResponse={rosterResponse} />}
        </>
      )}
    </>
  );
};

export default ComplianceTrainingRosterComponent;
